import { useState } from 'react'
import { X, Volume2, Zap, MessageSquare, Search as SearchIcon, Cpu, Settings as SettingsIcon, Monitor } from 'lucide-react'
import { LLMSettings } from './LLMSettings'
import { ActiveSessionsList } from './ActiveSessionsList'
import type { LLMConfiguration } from '../config/llmProviders'
import { PremiumDropdown } from './ui/PremiumDropdown'

type Settings = {
    voiceEnabled: boolean;
    voiceRate: number;
    voicePitch: number;
    selectedVoice: string;
    quickRepliesEnabled: boolean;
    autoSendFeedback: boolean;
    dragDropEnabled: boolean;
    threadingEnabled: boolean;
    searchEnabled: boolean;
};

interface SettingsModalProps {
    isOpen: boolean
    onClose: () => void
    settings: Settings
    onSettingsChange: (settings: Settings) => void
    llmConfig: LLMConfiguration
    onLLMConfigChange: (config: LLMConfiguration) => void
    activeTab?: Tab
    onTabChange?: (tab: Tab) => void
}

type Tab = 'general' | 'llm' | 'voice' | 'devices'

export function SettingsModal({
    isOpen,
    onClose,
    settings,
    onSettingsChange,
    llmConfig,
    onLLMConfigChange,
    activeTab: propActiveTab,
    onTabChange
}: SettingsModalProps) {
    const [localActiveTab, setLocalActiveTab] = useState<Tab>('general')
    const activeTab = propActiveTab || localActiveTab
    const setActiveTab = (tab: Tab) => {
        setLocalActiveTab(tab)
        onTabChange?.(tab)
    }

    if (!isOpen) return null

    const voices = typeof window !== 'undefined' && window.speechSynthesis
        ? window.speechSynthesis.getVoices().filter(v => v.lang.startsWith('pt'))
        : []

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Configurações</h2>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
                    <button
                        onClick={() => setActiveTab('general')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'general'
                            ? 'bg-white border-t-2 border-blue-600 text-blue-600'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                    >
                        <SettingsIcon className="w-4 h-4" />
                        Geral
                    </button>
                    <button
                        onClick={() => setActiveTab('llm')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'llm'
                            ? 'bg-white border-t-2 border-blue-600 text-blue-600'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                    >
                        <Cpu className="w-4 h-4" />
                        Modelos IA
                    </button>
                    <button
                        onClick={() => setActiveTab('voice')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'voice'
                            ? 'bg-white border-t-2 border-blue-600 text-blue-600'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                    >
                        <Volume2 className="w-4 h-4" />
                        Voz
                    </button>
                    <button
                        onClick={() => setActiveTab('devices')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'devices'
                            ? 'bg-white border-t-2 border-blue-600 text-blue-600'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                    >
                        <Monitor className="w-4 h-4" />
                        Dispositivos
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {activeTab === 'general' && (
                        <div className="space-y-6">
                            {/* Quick Replies Settings */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <Zap className="w-5 h-5 text-yellow-600" />
                                    <h3 className="text-md font-semibold text-gray-900">Respostas Rápidas</h3>
                                </div>

                                <div className="ml-7 space-y-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={settings.quickRepliesEnabled}
                                            onChange={(e) => onSettingsChange({ ...settings, quickRepliesEnabled: e.target.checked })}
                                            className="rounded text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">Mostrar respostas rápidas</span>
                                    </label>

                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={settings.autoSendFeedback}
                                            onChange={(e) => onSettingsChange({ ...settings, autoSendFeedback: e.target.checked })}
                                            className="rounded text-blue-600 focus:ring-blue-500"
                                            disabled={!settings.quickRepliesEnabled}
                                        />
                                        <span className="text-sm text-gray-700">Enviar feedback automaticamente</span>
                                    </label>
                                </div>
                            </div>

                            {/* File Upload Settings */}
                            <div className="space-y-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center gap-2">
                                    <MessageSquare className="w-5 h-5 text-green-600" />
                                    <h3 className="text-md font-semibold text-gray-900">Upload de Arquivos</h3>
                                </div>

                                <div className="ml-7 space-y-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={settings.dragDropEnabled}
                                            onChange={(e) => onSettingsChange({ ...settings, dragDropEnabled: e.target.checked })}
                                            className="rounded text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">Habilitar drag-and-drop</span>
                                    </label>
                                </div>
                            </div>

                            {/* Threading Settings */}
                            <div className="space-y-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center gap-2">
                                    <MessageSquare className="w-5 h-5 text-purple-600" />
                                    <h3 className="text-md font-semibold text-gray-900">Threading de Mensagens</h3>
                                </div>

                                <div className="ml-7 space-y-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={settings.threadingEnabled}
                                            onChange={(e) => onSettingsChange({ ...settings, threadingEnabled: e.target.checked })}
                                            className="rounded text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">Habilitar threads de mensagens</span>
                                    </label>
                                </div>
                            </div>

                            {/* Search Settings */}
                            <div className="space-y-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center gap-2">
                                    <SearchIcon className="w-5 h-5 text-indigo-600" />
                                    <h3 className="text-md font-semibold text-gray-900">Busca no Histórico</h3>
                                </div>

                                <div className="ml-7 space-y-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={settings.searchEnabled}
                                            onChange={(e) => onSettingsChange({ ...settings, searchEnabled: e.target.checked })}
                                            className="rounded text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">Habilitar busca avançada</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'llm' && (
                        <LLMSettings
                            currentConfig={llmConfig}
                            onConfigChange={onLLMConfigChange}
                        />
                    )}

                    {activeTab === 'voice' && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <Volume2 className="w-5 h-5 text-blue-600" />
                                <h3 className="text-md font-semibold text-gray-900">Configurações de Voz</h3>
                            </div>

                            <div className="ml-7 space-y-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={settings.voiceEnabled}
                                        onChange={(e) => onSettingsChange({ ...settings, voiceEnabled: e.target.checked })}
                                        className="rounded text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">Reproduzir respostas automaticamente</span>
                                </label>

                                <div>
                                    <PremiumDropdown
                                        label="Voz do Sistema"
                                        value={settings.selectedVoice}
                                        onChange={(value) => onSettingsChange({ ...settings, selectedVoice: value })}
                                        options={voices.map(voice => ({
                                            value: voice.name,
                                            label: voice.name
                                        }))}
                                        disabled={!settings.voiceEnabled}
                                        searchable={true}
                                        placeholder="Selecione uma voz"
                                    />
                                </div>

                                <div>
                                    <div className="flex justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-700">Velocidade</label>
                                        <span className="text-xs text-gray-500">{settings.voiceRate.toFixed(1)}x</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0.5"
                                        max="2"
                                        step="0.1"
                                        value={settings.voiceRate}
                                        onChange={(e) => onSettingsChange({ ...settings, voiceRate: parseFloat(e.target.value) })}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                                        disabled={!settings.voiceEnabled}
                                    />
                                </div>

                                <div>
                                    <div className="flex justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-700">Tom</label>
                                        <span className="text-xs text-gray-500">{settings.voicePitch.toFixed(1)}</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0.5"
                                        max="2"
                                        step="0.1"
                                        value={settings.voicePitch}
                                        onChange={(e) => onSettingsChange({ ...settings, voicePitch: parseFloat(e.target.value) })}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                                        disabled={!settings.voiceEnabled}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'devices' && (
                        <ActiveSessionsList />
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 shadow-sm transition-colors"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    )
}
